import React, { useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { createStyles, makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import MessagesMenu from './Messages'
import { ShopsMenu } from './Shops'
import { UserMenu } from './User'
import { AddOrderReturn } from './AddOrderReturnReg'
import { TimeRegistrationMenu } from './TimeRegistration'
import { HelpMenu } from './Help'
import { TopSearchV2 } from '@cbgms/components/src/top-search/search-v2/TopSearch'
import { useIsGarageAdmin, useIsPortal } from 'utils/Subscription/PermissionSelector'
import { RequiredPermission } from 'utils/Subscription/RequiredPermissions'
import { PERMISSION_CLOCKINGACTIONS_DAY, PERMISSION_CLOCKINGACTIONS_READ } from 'utils/Subscription/Permissions'
import SetupWizard from 'app/menu/components/TopBarV1/setupWizard/SetupWizard'
import { CarsysAppBar, CarsysAppBarProps } from 'app/menu/components/TopBar/AppBar'
import { CartMenu } from 'app/menu/components/TopBarV2/CartMenu'
import { TopSearchContextProvider } from 'app/menu/components/Search/TopSearchContextProvider'
import { useFeature } from '@cbgms/base/feature-flags'
import { useRouteMatch } from 'react-router-dom'
import { t } from 'i18next'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import DeliveryCountdown from './DeliveryCountdown'
import { OrderSystemFeature, isFeatureApplicable } from './orderSystemFeature'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    infoIconButton: {
      marginRight: theme.spacing(1),
      color: theme.palette.primaryThemeColor.contrastText,
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
        backgroundColor: 'transparent'
      }
    },
    searchBox: {
      width: '100%',
      maxWidth: '450px',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      position: 'relative'
    },
    searchBoxCollapsed: {
      width: '3%',
      padding: '6px',
      background: '#fff',
      borderRadius: '4px',
      justifyContent: 'center',
      boxShadow: theme.shadows[2],
      transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    searchIconOnly: {
      color: theme.palette.primary.main,
      fontSize: 28
    }
  })
)

export interface Props extends CarsysAppBarProps {}

export const TopBar: React.FC<Props> = ({ open, drawerWidthOpen, drawerWidthClosed, drawerVisible }) => {
  const classes = useStyles()
  const isPortal = useIsPortal()
  const { defaultOrderAccount } = useOrderAccountsContext()
  const orderSystem = useMemo(() => defaultOrderAccount?.Wholesaler?.OrderSystem, [defaultOrderAccount])
  const useV2 = useFeature('b2b-design-v2')
  const hasAxnlSplitProductSearch = useFeature('axnl-split-product-search')
  const hasB2BSearchOptimization = useFeature('b2b-search-optimization')
  const hasCreateOrderShortcut = useFeature('b2b-create-order-shortcut')
  const hasHeaderCutoffTimer = useFeature('b2b-fr0062-header-cutoff-timer')
  const isGarageAdmin = useIsGarageAdmin()
  const shouldShowSearch = !isGarageAdmin
  const orderMatch = useRouteMatch('/orders/:orderId')
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasQuery, setHasQuery] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    setIsExpanded(Boolean(orderMatch))
  }, [orderMatch])

  useEffect(() => {
    setCollapsed(Boolean(orderMatch) && !isExpanded)
  }, [orderMatch, isExpanded])

  const handleExpandSearch = () => {
    setIsExpanded(true)
  }

  const handleCollapseSearch = () => {
    if (orderMatch) {
      return
    }

    if (!hasQuery) {
      setIsExpanded(false)
    }
  }

  return (
    <CarsysAppBar open={open} drawerVisible={drawerVisible} drawerWidthOpen={drawerWidthOpen} drawerWidthClosed={drawerWidthClosed}>
      {shouldShowSearch && (
        <TopSearchContextProvider>
          {isPortal && hasAxnlSplitProductSearch ? (
            <Box
              ref={searchContainerRef}
              className={collapsed ? `${classes.searchBox} ${classes.searchBoxCollapsed}` : classes.searchBox}
              onFocus={collapsed ? handleExpandSearch : undefined}
              onClick={collapsed ? handleExpandSearch : undefined}
              onBlur={handleCollapseSearch}
              sx={collapsed ? { cursor: 'pointer' } : undefined}
              tabIndex={collapsed ? 0 : undefined}
            >
              {collapsed ? (
                <Box display='flex' alignItems='center' justifyContent='center' width='100%' height='100%'>
                  <TopSearchV2 isExpanded={orderMatch && isExpanded} iconOnly classes={{ icon: classes.searchIconOnly }} />
                </Box>
              ) : (
                <TopSearchV2 isExpanded={orderMatch && isExpanded} setHasQuery={setHasQuery} />
              )}
            </Box>
          ) : (
            <TopSearchV2 />
          )}
          {isPortal && hasB2BSearchOptimization && (
            <Tooltip title={t('dashboard.search-info')}>
              <IconButton size='medium' className={classes.infoIconButton}>
                <InfoOutlined fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </TopSearchContextProvider>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
        {isPortal && hasHeaderCutoffTimer && isFeatureApplicable(OrderSystemFeature.RouteDepartureCountdown, orderSystem) && (
          <DeliveryCountdown />
        )}
        <SetupWizard />
        <RequiredPermission permission={[PERMISSION_CLOCKINGACTIONS_READ, PERMISSION_CLOCKINGACTIONS_DAY]}>
          <TimeRegistrationMenu />
        </RequiredPermission>
        {isPortal && hasCreateOrderShortcut && <AddOrderReturn />}
        <MessagesMenu />
        <ShopsMenu />
        <UserMenu />
        <HelpMenu />
        {isPortal && !useV2 && <CartMenu />}
      </Box>
    </CarsysAppBar>
  )
}

export default TopBar
